import React from 'react';
import { Clock, Timer, TrendingUp, AlertCircle, CalendarCheck, CalendarX } from 'lucide-react';
import { Card, CardContent } from '../ui';
import { formatSeconds } from '../../utils/timeFormat';
import type { TimeRecordsStatistics } from '../../types/time-records.types';

interface TimeRecordsStatsProps {
  stats: TimeRecordsStatistics | null;
  isLoading?: boolean;
}

export function TimeRecordsStats({ stats, isLoading }: TimeRecordsStatsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {/* Total Worked Time */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold text-foreground">
                {formatSeconds(stats.totalWorkedSeconds)}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                Отработано
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Average Work Duration */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Timer className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold text-foreground">
                {formatSeconds(stats.averageWorkDurationSeconds)}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                Среднее за день
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Breaks */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <CalendarCheck className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold text-foreground">
                {formatSeconds(stats.totalBreakSeconds)}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                Перерывы
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Overtime */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <TrendingUp className="h-5 w-5 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold text-foreground">
                {formatSeconds(stats.totalOvertimeSeconds)}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                Сверхурочные
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Late Days */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold text-foreground">
                {stats.totalLateDays}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                Опоздания
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Early Leaves */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <CalendarX className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold text-foreground">
                {stats.totalEarlyLeaveDays}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                Ранние уходы
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
